import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Pin, PinOff, Trash2, MessageSquare, MoreHorizontal, Pencil, Search, X } from "lucide-react";
import { api } from "../../lib/api";
import { CHAT } from "../../constants/testIds";

export function ConversationSidebar({ activeId, onSelect, conversations, refresh, onClose, onDeleted }) {
  const [query, setQuery] = useState("");

  const newConvo = async () => {
    const { data } = await api.post("/conversations", { title: "Nova conversa" });
    await refresh();
    onSelect(data.id);
    onClose?.();
  };

  // Fixadas primeiro, depois mais recentes (o backend já manda assim; garante no cliente)
  const sorted = useMemo(() => {
    const list = [...conversations].sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => (c.title || "").toLowerCase().includes(q) || (c.subject || "").toLowerCase().includes(q));
  }, [conversations, query]);

  return (
    <div className="flex h-full flex-col bg-card border-r border-border">
      <div className="p-3 border-b border-border/60 space-y-2">
        <button
          data-testid={CHAT.newConversation}
          onClick={newConvo}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white py-2.5 text-sm font-medium transition"
        >
          <Plus className="h-4 w-4" /> Nova conversa
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            data-testid="chat-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar conversa..."
            className="w-full rounded-full bg-secondary pl-8 pr-8 py-2 text-xs outline-none focus:ring-2 focus:ring-violet-500/50"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-1">
        {sorted.length === 0 && (
          <p className="text-xs text-muted-foreground text-center mt-6 px-4">
            {query ? "Nenhuma conversa bate com a busca." : "Nenhuma conversa ainda. Clica em “Nova conversa” pra começar."}
          </p>
        )}
        {sorted.map((c) => (
          <ConversationItem
            key={c.id}
            convo={c}
            active={c.id === activeId}
            onSelect={() => { onSelect(c.id); onClose?.(); }}
            refresh={refresh}
            onDeleted={onDeleted}
          />
        ))}
      </div>
    </div>
  );
}

function ConversationItem({ convo, active, onSelect, refresh, onDeleted }) {
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(convo.title || "");
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (renaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renaming]);

  // Fecha o menu clicando fora
  useEffect(() => {
    if (!menu) return;
    const close = (e) => { if (!rootRef.current?.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const togglePin = async (e) => {
    e.stopPropagation();
    setMenu(false);
    await api.patch(`/conversations/${convo.id}`, { pinned: !convo.pinned });
    refresh();
  };

  const del = async (e) => {
    e.stopPropagation();
    setMenu(false);
    if (!window.confirm(`Apagar "${convo.title}"? Mensagens, materiais e flashcards dessa conversa vão junto.`)) return;
    await api.delete(`/conversations/${convo.id}`);
    onDeleted?.(convo.id);
    refresh();
  };

  const startRename = (e) => {
    e.stopPropagation();
    setMenu(false);
    setTitle(convo.title || "");
    setRenaming(true);
  };

  const commitRename = async () => {
    setRenaming(false);
    const t = title.trim();
    if (!t || t === convo.title) return;
    await api.patch(`/conversations/${convo.id}`, { title: t.slice(0, 60) });
    refresh();
  };

  return (
    <div
      ref={rootRef}
      data-testid={CHAT.conversationItem(convo.id)}
      onClick={renaming ? undefined : onSelect}
      onContextMenu={(e) => { e.preventDefault(); setMenu(true); }}
      className={`group relative flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition ${
        active ? "bg-violet-500/15 text-foreground" : "hover:bg-muted/60 text-muted-foreground"
      }`}
    >
      <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
      {renaming ? (
        <input
          ref={inputRef}
          data-testid={`chat-rename-input-${convo.id}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          }}
          className="flex-1 min-w-0 rounded bg-secondary px-2 py-1 text-sm outline-none ring-2 ring-violet-500/50"
        />
      ) : (
        <span className="flex-1 truncate text-sm">{convo.title}</span>
      )}
      {convo.pinned && !renaming && <Pin className="h-3 w-3 text-violet-400 shrink-0" />}
      {!renaming && (
        <button
          data-testid={CHAT.conversationOptions(convo.id)}
          onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition rounded p-1 hover:bg-background/50"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      )}
      {menu && (
        <div className="absolute right-2 top-full z-20 mt-1 w-40 rounded-lg border border-border bg-popover shadow-xl py-1 text-sm">
          <button
            data-testid={`chat-rename-${convo.id}`}
            onClick={startRename}
            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" /> Renomear
          </button>
          <button
            data-testid={CHAT.conversationPin(convo.id)}
            onClick={togglePin}
            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-foreground"
          >
            {convo.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            {convo.pinned ? "Desafixar" : "Fixar"}
          </button>
          <button
            data-testid={CHAT.conversationDelete(convo.id)}
            onClick={del}
            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" /> Excluir
          </button>
        </div>
      )}
    </div>
  );
}
